/**
 * @file useCascadeFadeIn.ts
 * @description Hook for cascading fade-in animations using Intersection Observer
 *
 * Provides smooth entrance animations for card grids and rails with staggered timing.
 * Only triggers when elements enter the viewport for performance.
 *
 * @example
 * ```tsx
 * const { ref, isVisible } = useCascadeFadeIn(index);
 *
 * <div
 *   ref={ref}
 *   className={cn(
 *     "transition-all duration-500 ease-out",
 *     isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
 *   )}
 * >
 *   <TitleCard ... />
 * </div>
 * ```
 */

import { useEffect, useRef, useState } from "react";

interface UseCascadeFadeInOptions {
  /**
   * Index in the list - used to calculate stagger delay
   */
  index: number;
  /**
   * How much of the element must be visible to trigger (0-1)
   * @default 0.1
   */
  threshold?: number;
  /**
   * Delay between each item in milliseconds
   * @default 50
   */
  staggerDelay?: number;
  /**
   * Whether the animation is enabled
   * @default true
   */
  enabled?: boolean;
}

interface UseCascadeFadeInReturn<T extends HTMLElement = HTMLDivElement> {
  /**
   * Ref to attach to the element
   */
  ref: React.RefObject<T | null>;
  /**
   * Whether the element is visible and should animate in
   */
  isVisible: boolean;
}

/**
 * Hook that provides cascading fade-in animation using Intersection Observer
 *
 * @param options - Configuration options
 * @returns Object with ref and isVisible state
 */
export function useCascadeFadeIn<T extends HTMLElement = HTMLDivElement>({
  index,
  threshold = 0.1,
  staggerDelay = 50,
  enabled = true,
}: UseCascadeFadeInOptions): UseCascadeFadeInReturn<T> {
  const [isVisible, setIsVisible] = useState(!enabled); // If disabled, always visible
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Stagger based on index
          setTimeout(() => {
            setIsVisible(true);
          }, index * staggerDelay);
        }
      },
      { threshold },
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [index, threshold, staggerDelay, enabled]);

  return { ref, isVisible };
}
